import { useState } from "react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

export default function AdminLeads() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!adminKey) { toast.error("Enter your admin key."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { headers: { "X-Admin-Key": adminKey } });
      setLeads(data.leads || []);
      setLoaded(true);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Invalid admin key." : "Could not load leads.");
    } finally { setLoading(false); }
  };

  const downloadCsv = () => {
    if (!leads.length) { toast.error("No leads to export."); return; }
    const rows = [["Email", "Source", "Signed up"], ...leads.map((l) => [l.email, l.source || "", l.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `kurti-connection-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} emails`);
  };

  const inputCls = "border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[900px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-leads-page">
      <Seo title="Leads Admin" path="/admin/leads" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Captured Emails</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">Emails collected from the sale popup. Download as CSV to import into Shopify Email, Klaviyo, or Mailchimp.</p>

      <div className="flex flex-wrap items-end gap-3 mb-8">
        <label className="flex flex-col gap-1.5">
          <span className="overline text-[#1A1A1A]">Admin Key</span>
          <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
            data-testid="admin-key-input" className={`${inputCls} w-64`} />
        </label>
        <button onClick={load} disabled={loading} data-testid="load-leads-button"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-6 py-2.5 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading" : "Load"}
        </button>
        {loaded && (
          <button onClick={downloadCsv} data-testid="download-csv-button"
            className="inline-flex items-center gap-2 border border-[#1A1A1A] px-6 py-2.5 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <div data-testid="leads-table">
          <p className="text-sm text-[#6E6E69] mb-3 font-body">{leads.length} email{leads.length === 1 ? "" : "s"} captured</p>
          {leads.length === 0 ? (
            <div className="border border-dashed border-[#E5E5DF] px-4 py-10 text-center text-sm text-[#6E6E69]">No emails captured yet.</div>
          ) : (
            <div className="border border-[#E5E5DF] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A1A1A] text-white text-left">
                    <th className="px-4 py-2.5 font-mono text-[0.66rem] uppercase tracking-[0.08em] font-normal">Email</th>
                    <th className="px-4 py-2.5 font-mono text-[0.66rem] uppercase tracking-[0.08em] font-normal">Source</th>
                    <th className="px-4 py-2.5 font-mono text-[0.66rem] uppercase tracking-[0.08em] font-normal">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={l.email} className={i % 2 ? "bg-[#F3F2EC]" : "bg-white"}>
                      <td className="px-4 py-2.5 font-body text-[#1A1A1A]">{l.email}</td>
                      <td className="px-4 py-2.5 font-mono text-[0.72rem] text-[#6E6E69]">{l.source}</td>
                      <td className="px-4 py-2.5 font-mono text-[0.72rem] text-[#6E6E69]">{(l.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
